import { useTranslation } from "react-i18next";
import { Trans } from "../../Translate";
import React, { useMemo } from "react";
import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";

import { IconPlus } from "../../icons";
import { createTestId } from "../../TestableElement";
import type { TaskBoard } from "../components";
import type { TaskBoardProps } from "../types";
import { Column } from "./Column";
import type { KanbanStatus, KanbanToolbarContext } from "./types";
import { StatusSelector } from "../../StatusSelector";
import { useHorizontalAutoScroll, useSortableItem, DropIndicator } from "../../utils/PragmaticDragAndDrop";
import type { BoardLocation } from "../../utils/PragmaticDragAndDrop";
import classNames from "../../utils/classnames";
import { useTaskKeyboardNavigation } from "../hooks/useTaskKeyboardNavigation";
import { TaskDisplayMenu } from "../components/TaskDisplayMenu";
import { useBoardColumnColorMode } from "../hooks/useBoardColumnColorMode";

interface Props {
  fillHeight?: boolean;
  archiveView?: KanbanToolbarContext["archiveView"];
  milestone: TaskBoard.Milestone | null;
  columns: Record<KanbanStatus, TaskBoard.Task[]>;
  draggedItemId: string | null;
  targetLocation: BoardLocation | null;
  placeholderHeight: number | null;
  statuses: StatusSelector.StatusOption[];
  onTaskAssigneeChange?: TaskBoardProps["onTaskAssigneeChange"];
  onTaskDueDateChange?: TaskBoardProps["onTaskDueDateChange"];
  onTaskDueOffsetDaysChange?: TaskBoardProps["onTaskDueOffsetDaysChange"];
  assigneePersonSearch?: TaskBoardProps["assigneePersonSearch"];
  onTaskCreate?: TaskBoardProps["onTaskCreate"];
  onAddStatusClick?: () => void;
  onEditStatus?: (status: StatusSelector.StatusOption) => void;
  onDeleteStatus?: (status: StatusSelector.StatusOption) => void;
  onTaskClick: (taskId: string) => void;
  isTaskSlideInOpen: boolean;
  canEdit: boolean;
  canManageStatuses?: boolean;
  renderHeader?: (actions: React.ReactNode) => React.ReactNode;
  toolbarLeading?: React.ReactNode;
  toolbarActions?: React.ReactNode | ((context: KanbanToolbarContext) => React.ReactNode);
}

export function Kanban({
  fillHeight = false,
  archiveView,
  milestone,
  columns,
  draggedItemId,
  targetLocation,
  placeholderHeight,
  statuses,
  onTaskAssigneeChange,
  onTaskDueDateChange,
  onTaskDueOffsetDaysChange,
  assigneePersonSearch,
  onTaskCreate,
  onAddStatusClick,
  onEditStatus,
  onDeleteStatus,
  onTaskClick,
  isTaskSlideInOpen,
  canEdit,
  canManageStatuses = false,
  toolbarLeading,
  toolbarActions,
  renderHeader,
}: Props) {
  const { t } = useTranslation();
  const testId = useMemo(
    () => (milestone ? createTestId("milestone", milestone.id) : "kanban-no-milestone"),
    [milestone],
  );
  const scrollContainerRef = useHorizontalAutoScroll();
  const {
    containerRef: keyboardContainerRef,
    selectedTaskId: keyboardSelectedTaskId,
    scopeBind,
  } = useTaskKeyboardNavigation<HTMLDivElement>({
    fieldShortcuts: { status: false, create: Boolean(canEdit && onTaskCreate) },
    clearSelectionWithEscape: !isTaskSlideInOpen,
  });

  const unknownStatus = statuses.find((status) => status.value === "unknown-status");
  const [colorMode, setColorMode] = useBoardColumnColorMode();
  const columnColors = { value: colorMode, onChange: setColorMode };
  const regularStatuses = statuses.filter((status) => status.value !== "unknown-status");
  const closedStatuses = regularStatuses.filter((status) => status.closed);
  const [areClosedStatusesVisible, setAreClosedStatusesVisible] = React.useState(true);

  const toolbarContext: KanbanToolbarContext = {
    archiveView,
    columnColors,
    closedStatuses: {
      count: archiveView?.archived ? 0 : closedStatuses.length,
      visible: areClosedStatusesVisible,
      onVisibilityChange: setAreClosedStatusesVisible,
    },
  };
  const resolvedToolbarActions =
    typeof toolbarActions === "function"
      ? toolbarActions(toolbarContext)
      : toolbarActions || <TaskDisplayMenu {...toolbarContext} />;
  const hasBoardToolbar = !renderHeader && Boolean(toolbarLeading || resolvedToolbarActions);

  const setScrollContainerRefs = React.useCallback(
    (element: HTMLDivElement | null) => {
      (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
      (keyboardContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
    },
    [keyboardContainerRef, scrollContainerRef],
  );

  const handleTaskCreate = (title: string, statusValue: string) => {
    if (!onTaskCreate) return;

    const status = statuses.find((s) => s.value === statusValue);

    onTaskCreate({
      title,
      milestone,
      dueDate: null,
      assignees: [],
      status: status,
    });
  };

  const renderStatusColumn = (status: StatusSelector.StatusOption, index: number) => (
    <SortableStatusColumn
      key={status.value}
      status={status}
      index={index}
      canReorder={canManageStatuses}
      fillHeight={fillHeight}
    >
      {(dragHandleRef) => (
        <Column
          fillHeight={fillHeight}
          status={status}
          colorMode={columnColors.value}
          tasks={columns[status.value] || []}
          draggedItemId={draggedItemId}
          targetLocation={targetLocation}
          placeholderHeight={placeholderHeight}
          onTaskAssigneeChange={canEdit ? onTaskAssigneeChange : undefined}
          onTaskDueDateChange={canEdit ? onTaskDueDateChange : undefined}
          onTaskDueOffsetDaysChange={canEdit ? onTaskDueOffsetDaysChange : undefined}
          assigneePersonSearch={canEdit ? assigneePersonSearch : undefined}
          onCreateTask={onTaskCreate ? (title) => handleTaskCreate(title, status.value) : undefined}
          dragHandleRef={dragHandleRef}
          isStatusDraggable={canManageStatuses}
          allStatuses={statuses}
          canManageStatuses={canManageStatuses}
          disableDnD={!canEdit}
          canCreateTask={canEdit}
          canEdit={canEdit}
          onEditStatus={onEditStatus}
          onDeleteStatus={onDeleteStatus}
          onTaskClick={onTaskClick}
          selectedTaskId={keyboardSelectedTaskId}
        />
      )}
    </SortableStatusColumn>
  );

  return (
    <>
      {renderHeader?.(resolvedToolbarActions)}
      <section
        className={classNames("bg-surface-base", fillHeight ? "flex flex-col flex-1 min-h-0" : "min-h-[80vh]")}
        data-test-id={testId}
      >
        {hasBoardToolbar && (
          <div
            className={classNames(
              "mx-4 mt-4 flex min-h-9 flex-wrap items-center gap-3 px-1",
              fillHeight && "shrink-0",
              toolbarLeading ? "justify-between" : "justify-end",
            )}
            data-test-id="kanban-toolbar"
          >
            {toolbarLeading}

            <div className="flex items-center gap-1">{resolvedToolbarActions}</div>
          </div>
        )}

        <div
          ref={setScrollContainerRefs}
          className={classNames(
            "px-3 pb-3 pt-3",
            fillHeight
              ? "flex-1 min-h-0 overflow-auto"
              : classNames("overflow-x-auto", hasBoardToolbar ? "h-[calc(80vh-52px)]" : "h-[80vh]"),
          )}
          data-test-id="kanban-scroll-container"
          {...scopeBind}
        >
          {archiveView?.archived && statuses.length === 0 && (
            <div className="p-8 text-center text-content-dimmed" data-test-id="archive-empty-state">
              {t("No archived tasks.")}
            </div>
          )}
          <div
            className={classNames(
              "flex gap-3 min-w-max",
              fillHeight && statuses.length > 0 ? "min-h-full items-stretch" : "items-start",
            )}
            data-test-id="kanban-columns"
          >
            {unknownStatus && (
              <Column
                fillHeight={fillHeight}
                status={unknownStatus}
                colorMode="none"
                tasks={columns[unknownStatus.value] || []}
                draggedItemId={draggedItemId}
                targetLocation={targetLocation}
                placeholderHeight={placeholderHeight}
                onTaskAssigneeChange={canEdit ? onTaskAssigneeChange : undefined}
                onTaskDueDateChange={canEdit ? onTaskDueDateChange : undefined}
                onTaskDueOffsetDaysChange={canEdit ? onTaskDueOffsetDaysChange : undefined}
                assigneePersonSearch={canEdit ? assigneePersonSearch : undefined}
                onCreateTask={undefined}
                dragHandleRef={undefined}
                isStatusDraggable={false}
                allStatuses={statuses}
                canManageStatuses={false}
                onEditStatus={undefined}
                onDeleteStatus={undefined}
                onTaskClick={onTaskClick}
                canCreateTask={canEdit}
                canEdit={canEdit}
                selectedTaskId={keyboardSelectedTaskId}
                hideStatusIcon
                disableDnD
              />
            )}

            {regularStatuses.map((status, index) => {
              if (status.closed && !areClosedStatusesVisible && !archiveView?.archived) return null;

              return renderStatusColumn(status, index);
            })}

            {canManageStatuses && onAddStatusClick && <AddStatusButton onClick={onAddStatusClick} />}
          </div>
        </div>
      </section>
    </>
  );
}

interface AddStatusButtonProps {
  onClick: () => void;
}

function AddStatusButton({ onClick }: AddStatusButtonProps) {
  return (
    <button
      type="button"
      className="flex w-48 flex-shrink-0 self-start items-center gap-2 rounded-lg border border-dashed border-surface-outline px-3 py-3 text-left text-sm font-medium text-content-dimmed transition-colors hover:bg-surface-dimmed hover:text-content-base"
      onClick={onClick}
      data-test-id="add-status"
    >
      <Trans i18nKey="<icon/>Add status" components={{ icon: <IconPlus size={16} /> }} />
    </button>
  );
}

interface SortableStatusColumnProps {
  fillHeight: boolean;
  status: StatusSelector.StatusOption;
  index: number;
  canReorder: boolean;
  children: (dragHandleRef: React.RefObject<HTMLDivElement>) => React.ReactNode;
}

function SortableStatusColumn({ status, index, canReorder, children, fillHeight }: SortableStatusColumnProps) {
  const { ref, dragHandleRef, isDragging, closestEdge } = useSortableItem({
    itemId: status.value,
    index,
    containerId: "status-columns",
    scope: "status-columns",
    disabled: !canReorder,
    allowedEdges: ["left", "right"],
  });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={classNames("relative flex-shrink-0", fillHeight && "flex", isDragging && "z-10")}
    >
      {closestEdge && <DropIndicator edge={closestEdge as Edge} />}
      {children(dragHandleRef as React.RefObject<HTMLDivElement>)}
    </div>
  );
}
